// src/services/emailService.js
// Handles sending emails via EmailJS
// Free plan: 200 emails/month
// Sign up at emailjs.com

// ─────────────────────────────────────────────
// HOW TO SET UP EMAILJS (5 minutes):
//
// 1. Go to emailjs.com and create a free account
// 2. Click "Add New Service" → choose Gmail or any email
// 3. Connect your email (info@revfoundation.org)
// 4. Note your SERVICE ID
//
// 5. Click "Email Templates" → "Create New Template"
// 6. Create TWO templates (see below for content)
// 7. Note each TEMPLATE ID
//
// 8. Click "Account" → note your PUBLIC KEY
//
// 9. Add to your .env file:
//    REACT_APP_EMAILJS_SERVICE_ID=your_service_id
//    REACT_APP_EMAILJS_PUBLIC_KEY=your_public_key
//    REACT_APP_EMAILJS_WELCOME_TEMPLATE=your_welcome_template_id
//    REACT_APP_EMAILJS_MEMBERSHIP_TEMPLATE=your_membership_template_id
// ─────────────────────────────────────────────

import { init, send as emailjsSend } from "@emailjs/browser";

const EMAILJS_SERVICE_ID          = "service_atbqn6o";
const EMAILJS_PUBLIC_KEY          = "uo9bCnw_kdZbDKRID";
const EMAILJS_WELCOME_TEMPLATE    = "template_whozvjl";
const EMAILJS_MEMBERSHIP_TEMPLATE = "template_zbluzev";

// Initialize EmailJS SDK (safe to call multiple times)
try { init(EMAILJS_PUBLIC_KEY); } catch (e) { /* ignore init errors */ }

/**
 * Send an email via EmailJS (using the @emailjs/browser package)
 */
async function sendEmail(templateId, templateParams) {
  if (!EMAILJS_SERVICE_ID || !EMAILJS_PUBLIC_KEY || !templateId) {
    console.warn("EmailJS not configured. Skipping email.");
    return;
  }
  try {
    const res = await emailjsSend(EMAILJS_SERVICE_ID, templateId, templateParams);
    console.log("Email sent successfully:", templateId, res);
  } catch (err) {
    // Don't crash the app if email fails — just log it
    console.error("Email send failed:", err);
  }
}

// ─────────────────────────────────────────────
// EMAIL 1: WELCOME ON SIGNUP
// ─────────────────────────────────────────────
// EmailJS Template content for WELCOME email:
//
// Subject: Welcome to Revolutionary Foundation, {{member_name}}!
//
// Body:
// Dear {{member_name}},
//
// Welcome to the Revolutionary Foundation family! 🎉
//
// Your account has been successfully created.
//
// Member ID: {{member_id}}
// Email: {{member_email}}
// State: {{member_state}}
//
// NEXT STEP — Activate Your Membership:
// To access grant applications, please complete your
// one-time ₦250 membership payment.
//
// Payment Details:
// Bank: First Bank Nigeria
// Account Name: Revolutionary Foundation
// Account Number: 3012345678
// Amount: ₦250
// Reference: {{member_id}}
//
// Or pay instantly online at:
// https://revolutionary-foundation.web.app/membership
//
// Once payment is confirmed, you can immediately
// apply for grants ranging from ₦80,000 to ₦50,000,000.
//
// Thank you for joining us in transforming communities
// across Nigeria.
//
// Warm regards,
// Revolutionary Foundation Team
// info@revfoundation.org
// ─────────────────────────────────────────────

export async function sendWelcomeEmail({ name, email, memberId, state }) {
  await sendEmail(EMAILJS_WELCOME_TEMPLATE, {
    member_name:  name,
    member_email: email,
    member_id:    memberId,
    member_state: state  || "Nigeria",
    payment_link: "https://revolutionary-foundation.web.app/membership",
    dashboard_link: "https://revolutionary-foundation.web.app/dashboard",
    year:         new Date().getFullYear(),
  });
}

// ─────────────────────────────────────────────
// EMAIL 2: MEMBERSHIP ACTIVATED AFTER PAYMENT
// ─────────────────────────────────────────────
// EmailJS Template content for MEMBERSHIP ACTIVATED email:
//
// Subject: 🎉 Membership Activated — You can now apply for grants!
//
// Body:
// Dear {{member_name}},
//
// Congratulations! Your membership has been activated. ✅
//
// MEMBERSHIP DETAILS:
// Member ID:        {{member_id}}
// Payment Amount:   ₦250
// Payment Date:     {{payment_date}}
// Payment Ref:      {{payment_ref}}
// Status:           ACTIVE
//
// YOU CAN NOW:
// ✅ Apply for grants (₦80,000 – ₦50,000,000)
// ✅ Access your member dashboard
// ✅ Track your applications in real time
// ✅ Receive notifications on grant decisions
//
// AVAILABLE GRANT PROGRAMS:
// • Community Seed Grant: ₦80,000 – ₦200,000
// • Development Acceleration: ₦5,000,000 – ₦20,000,000
// • Impact Partnership Grant: ₦10,000,000 – ₦50,000,000
// • Emergency Relief Grant: ₦50,000 – ₦250,000
//
// Start your application here:
// {{apply_link}}
//
// Or visit your dashboard:
// {{dashboard_link}}
//
// Thank you for believing in the power of community.
//
// Warm regards,
// Revolutionary Foundation Team
// info@revfoundation.org
// ─────────────────────────────────────────────

export async function sendMembershipActivatedEmail({
  name, email, memberId, paymentRef,
}) {
  await sendEmail(EMAILJS_MEMBERSHIP_TEMPLATE, {
    member_name:   name,
    member_email:  email,
    member_id:     memberId,
    payment_ref:   paymentRef,
    payment_date:  new Date().toLocaleDateString("en-GB", {
      day: "2-digit", month: "long", year: "numeric",
    }),
    payment_amount: "₦250",
    apply_link:    "https://revolutionary-foundation.web.app/apply",
    dashboard_link:"https://revolutionary-foundation.web.app/dashboard",
    year:          new Date().getFullYear(),
  });
}
